<template>
  <BaseCard type="filled" class="relative flex flex-col p-3">
    <div class="flex flex-row items-center">
      <div class="relative mr-4 h-16 w-16 shrink-0">
        <div class="animate-rotate">
          <BaseShape
            shape="7-sided-cookie"
            class="animate-bouncy-spin text-primary h-full w-full scale-120"
          />
        </div>
        <BaseIcon
          size="32"
          class="text-on-primary absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          >{{ feedbackTopic.icon }}</BaseIcon
        >
      </div>
      <div class="flex grow flex-col">
        <p class="text-body-medium text-on-surface-variant mb-0.5">
          Feedbackauftrag · <TimeAgo :date="task.createdAt" /><span v-if="task.recurrence">
            · {{ $t('frequency.' + task.recurrence.frequency, task.recurrence.interval) }}</span
          >
        </p>
        <h3 class="text-title-large">{{ feedbackTopic.cta }}</h3>
        <p v-if="plainText" class="text-body-medium text-on-surface-variant mt-1">
          {{ plainText }}
        </p>
        <div class="mt-1.5 flex flex-row items-center">
          <BaseCircularProgressIndictator
            class="mr-1 shrink-0"
            :percentage="(numberOfGivenFeedbacks / task.numberOfQuestions) * 100"
            :radius="10"
            :thickness="4"
          />
          <span
            class="text-body-medium text-on-surface-variant inline-flex flex-row items-center gap-1 overflow-hidden text-ellipsis whitespace-nowrap"
          >
            {{ task.numberOfQuestions }} {{ $t('common.question', task.numberOfQuestions) }}
            {{ $t('common.estimatedTime', { count: task.timeEstimate }) }} ·
            {{ feedbackTopic.title }} · Wird geteilt mit:
            <BaseMemberAvatar class="inline-flex!" size="24" :member="task.owner" />{{
              task.owner.firstName
            }}
          </span>
        </div>
      </div>
      <BaseButtonGroup class="self-center">
        <BaseMenu v-if="accepted" :items="menuItems" v-slot="{ open }">
          <BaseIconButton icon="more_vert" size="M" width="narrow" :force-pressed="open" />
        </BaseMenu>
        <BaseIconButton
          v-else
          icon="close"
          size="M"
          color="outlined"
          width="narrow"
          @click="openDialog"
        />
        <BaseTextButton size="M" color="tonalPrimary" @click="accept" width="wide"
          >Starten</BaseTextButton
        >
      </BaseButtonGroup>
    </div>
    <BaseDialog
      type="basic"
      :is-open="isOpen"
      @close="closeDialog"
      title="Anfrage ablehnen"
      description="Möchtest du die Feedbackanfrage wirklich ablehnen?"
    >
      <BaseInputField
        class="bg-surface-container-high mt-2"
        label="Begründung"
        type="multiText"
        v-model="rejectionReason"
      />
      <template #confirm
        ><BaseTextButton @click="clickReject">Anfrage ablehnen</BaseTextButton></template
      >
      <template #dismiss
        ><BaseTextButton @click="closeDialog">{{ $t('common.cancel') }}</BaseTextButton></template
      >
    </BaseDialog>
  </BaseCard>
</template>

<script setup lang="ts">
  import type { FeedbackTask } from '@/modules/task/composables/useFeedbackTasks'
  import { computed, ref } from 'vue'
  import BaseCard from '../base/card/BaseCard.vue'
  import { OpenFeedbackState } from '@/modules/open-feedbacks/composables/useOpenFeedbacks'
  import BaseShape from '../base/shapes/BaseShape.vue'
  import { useUserStore } from '@/stores/user.store'
  import TimeAgo from '../base/time-ago/TimeAgo.vue'
  import { useLocalFeedbacks } from '@/core/composables/useLocalFeedbacks'
  import BaseCircularProgressIndictator from '../base/circlular-progress-indicator/BaseCircularProgressIndictator.vue'
  import BaseTextButton from '../base/button/text-button/BaseTextButton.vue'
  import BaseIconButton from '../base/button/icon-button/BaseIconButton.vue'
  import { useI18n } from 'vue-i18n'
  import BaseMenu from '../base/menu/BaseMenu.vue'
  import BaseButtonGroup from '../base/button/BaseButtonGroup.vue'
  import BaseDialog from '../base/dialog/BaseDialog.vue'
  import { useDialog } from '../base/dialog/use-dialog'
  import { useMutateFeedbackRequest } from '@/core/composables/use-mutate-feedback-request'
  import BaseInputField from '../base/form-fields/BaseInputField.vue'
  import BaseIcon from '../base/icon/BaseIcon.vue'
  import { useWysiwygEditor } from '@/core/composables/useWysiwygEditor'
  import BaseMemberAvatar from '../base/avatar/BaseMemberAvatar.vue'

  const props = defineProps<{
    task: FeedbackTask
  }>()

  const feedbackTopic = computed(() => {
    return props.task.feedbackTopics[0]
  })

  const accepted = computed(() => {
    return props.task.surveyTandems.every(
      (tandem) => tandem.senderState === OpenFeedbackState.InProgress,
    )
  })

  const { user } = useUserStore()

  const { accept, reject } = useMutateFeedbackRequest({
    feedbackReceiverId: user.id,
    surveyId: props.task.surveyId,
  })

  const clickReject = () => {
    reject(rejectionReason.value)
    closeDialog()
  }

  const rejectionReason = ref<string | undefined>(undefined)

  const { feedbacksBySurveyIdAndReceiverId } = useLocalFeedbacks()
  const feedbacks = feedbacksBySurveyIdAndReceiverId(props.task.surveyId, user.id)

  const numberOfGivenFeedbacks = computed(() => {
    return feedbacks.value.length
  })

  const { convertJSONToPlainText } = useWysiwygEditor()

  const plainText = computed(() => {
    if (!props.task.message) return undefined

    const jsonPlainText = convertJSONToPlainText(props.task.message)

    if (!jsonPlainText) return undefined

    return jsonPlainText
  })

  const { isOpen, openDialog, closeDialog } = useDialog()

  const { t } = useI18n()

  const menuItems = computed(() => {
    return [
      {
        id: 'delete',
        title: t('common.cancel'),
        leadingIcon: 'close',
        callback: () => {
          openDialog()
        },
      },
    ]
  })
</script>
